import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { avatarModelForPlayer } from '../game/avatarModel';

const defaultStats = {
  gamesPlayed: 0,
  gamesWon: 0,
  handsPlayed: 0,
  netChips: 0
};

function formatNumber(value) {
  return Number(value || 0).toLocaleString();
}

function formatSigned(value) {
  const number = Number(value || 0);
  return `${number >= 0 ? '+' : ''}${number.toLocaleString()}`;
}

function formatDate(value) {
  if (!value) {
    return '-';
  }
  return new Date(value).toLocaleString();
}

const ProfileStatsPage = () => {
  const { user, uploadAvatar, deleteAvatar } = useAuth();
  const [stats, setStats] = useState(defaultStats);
  const [recentGames, setRecentGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarError, setAvatarError] = useState('');

  const avatar = avatarModelForPlayer(user);
  const hasAvatar = Boolean(avatar.src);

  useEffect(() => {
    let active = true;

    async function loadStats() {
      setLoading(true);
      setError('');
      try {
        const body = await apiRequest('/api/stats/me');
        if (!active) {
          return;
        }
        setStats(body.stats || defaultStats);
        setRecentGames(Array.isArray(body.recentGames) ? body.recentGames : []);
      } catch (requestError) {
        if (active) {
          setError(requestError.message || '无法加载统计');
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadStats();
    return () => {
      active = false;
    };
  }, []);

  async function handleAvatarUpload(event) {
    const [file] = Array.from(event.target.files || []);
    event.target.value = '';

    if (!file) {
      return;
    }

    setAvatarBusy(true);
    setAvatarError('');
    try {
      await uploadAvatar(file);
    } catch (requestError) {
      setAvatarError(requestError.message || '头像上传失败');
    } finally {
      setAvatarBusy(false);
    }
  }

  async function handleAvatarDelete() {
    setAvatarBusy(true);
    setAvatarError('');
    try {
      await deleteAvatar();
    } catch (requestError) {
      setAvatarError(requestError.message || '头像删除失败');
    } finally {
      setAvatarBusy(false);
    }
  }

  return (
    <main className="page-shell page-shell--themed stats-page">
      <header className="page-hero room-header">
        <div>
          <p className="eyebrow">个人资料</p>
          <h1>个人统计</h1>
          <p className="muted">已完成游戏和筹码变化。</p>
        </div>
        <Link className="secondary-button secondary-button--stone link-button" to="/">游戏库</Link>
      </header>

      {error && <div className="error-message">{error}</div>}

      <section className="profile-avatar-card data-card" aria-label="头像设置">
        <div className="profile-avatar-card__preview">
          {hasAvatar ? (
            <img
              alt={avatar.alt}
              className="profile-avatar-preview"
              src={avatar.src}
            />
          ) : (
            <span className="profile-avatar-preview profile-avatar-preview--fallback">
              {avatar.fallbackLabel}
            </span>
          )}
        </div>
        <div className="profile-avatar-card__body">
          <div className="section-heading">
            <h2>头像</h2>
            <span>{user?.displayName || '玩家'}</span>
          </div>
          <p className="muted">上传后会同步显示在房间和牌桌座位。</p>
          <label className="field-label" htmlFor="profile-avatar-upload">
            上传头像
            <input
              accept="image/*"
              aria-label="上传头像"
              disabled={avatarBusy}
              id="profile-avatar-upload"
              onChange={handleAvatarUpload}
              type="file"
            />
          </label>
          <div className="profile-avatar-card__actions">
            <button
              className="secondary-button"
              disabled={avatarBusy || !hasAvatar}
              onClick={handleAvatarDelete}
              type="button"
            >
              删除头像
            </button>
            {avatarBusy && <span className="muted">处理中...</span>}
          </div>
          {avatarError && <div className="error-message">{avatarError}</div>}
        </div>
      </section>

      <section className="metrics-grid" aria-label="汇总统计">
        <article className="metric-tile data-card">
          <span>参与局数</span>
          <strong>{formatNumber(stats.gamesPlayed)}</strong>
        </article>
        <article className="metric-tile data-card">
          <span>获胜局数</span>
          <strong>{formatNumber(stats.gamesWon)}</strong>
        </article>
        <article className="metric-tile data-card">
          <span>参与手数</span>
          <strong>{formatNumber(stats.handsPlayed)}</strong>
        </article>
        <article className="metric-tile data-card">
          <span>净筹码</span>
          <strong className={Number(stats.netChips || 0) >= 0 ? 'net-positive' : 'net-negative'}>
            {formatSigned(stats.netChips)}
          </strong>
        </article>
      </section>

      <section className="data-section data-card">
        <div className="section-heading">
          <h2>最近游戏</h2>
          <span>{loading ? '加载中' : `显示 ${recentGames.length} 条`}</span>
        </div>

        {recentGames.length === 0 && !loading ? (
          <p className="empty-line">还没有已完成的游戏。</p>
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>场次</th>
                  <th>开始时间</th>
                  <th>结束时间</th>
                  <th>最终筹码</th>
                  <th>净变化</th>
                  <th>排名</th>
                </tr>
              </thead>
              <tbody>
                {recentGames.map((game) => (
                  <tr key={game.sessionId}>
                    <td>#{game.sessionId}</td>
                    <td>{formatDate(game.startedAt)}</td>
                    <td>{formatDate(game.endedAt)}</td>
                    <td>{formatNumber(game.finalChips)}</td>
                    <td className={Number(game.netChips || 0) >= 0 ? 'net-positive' : 'net-negative'}>
                      {formatSigned(game.netChips)}
                    </td>
                    <td>#{game.rank}</td>
                  </tr>
                ))}
                {loading && (
                  <tr>
                    <td colSpan="6">正在加载统计...</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </main>
  );
};

export default ProfileStatsPage;
